import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "./hooks";

// The motion of the airports the possible-airports clue marks.
// They are drawn inside the map, where CSS cannot animate them, so their size
// and strength are worked out here, frame by frame.

// They pop in one after another, each overshooting a little, like the guess
// pins dropping in (ms).
const POP_DELAY = 400;
const POP_STAGGER = 120;
const POP_TAKES = 450;
// While a guess is checked they fade to this, over this long.
export const MARKED_FADED = 0.25;
const FADE_TAKES = 400;

// Rises past 1 and settles back, like cubic-bezier(0.2, 0.9, 0.3, 1.4).
const overshoot = (t: number) => {
  const pull = 1.70158;
  return 1 + (pull + 1) * (t - 1) ** 3 + pull * (t - 1) ** 2;
};
const easeOut = (t: number) => 1 - (1 - t) ** 3;
const clamp = (t: number) => Math.min(1, Math.max(0, t));

// `key` changes when a new set of airports appears; `faded` is whether they
// should be faded now. Returns each airport's size and strength by its place
// in the list.
export const useMarkedMotion = (key: string, count: number, faded: boolean) => {
  const [popClock, setPopClock] = useState(0);
  const [fade, setFade] = useState(1);
  const fadeNow = useRef(1);

  useEffect(() => {
    if (!key) {
      setPopClock(0);
      return;
    }
    if (prefersReducedMotion()) {
      setPopClock(Infinity);
      return;
    }
    const startedAt = performance.now();
    const lasts = POP_DELAY + count * POP_STAGGER + POP_TAKES;
    let frame = 0;
    const tick = (now: number) => {
      const elapsed = now - startedAt;
      setPopClock(elapsed);
      if (elapsed < lasts) frame = requestAnimationFrame(tick);
    };
    setPopClock(0);
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // A new set of airports restarts it; nothing else does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const from = fadeNow.current;
    const to = faded ? MARKED_FADED : 1;
    if (from === to) return;
    if (prefersReducedMotion()) {
      fadeNow.current = to;
      setFade(to);
      return;
    }
    const startedAt = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = clamp((now - startedAt) / FADE_TAKES);
      fadeNow.current = from + (to - from) * easeOut(t);
      setFade(fadeNow.current);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [faded]);

  return (index: number) => {
    const t = clamp((popClock - POP_DELAY - index * POP_STAGGER) / POP_TAKES);
    return { scale: t === 1 ? 1 : Math.max(0, overshoot(t)), opacity: Math.min(1, t * 2) * fade };
  };
};
